<template>
  <div class="personal-counter-content">
    <p
      v-if="betaUser"
      class="personal-counter-content__notify"
      data-test-id="personal-counter-content-notify"
    >
      {{ title }}
    </p>

    <p class="personal-counter-content__body" data-test-id="personal-counter-content-body">
      <span v-safe-html="bodyText" />
      <a
        v-if="!loginFailed"
        class="link link--learn-more"
        :href="learnMoreLink"
        :data-track-id="trackIds.learnMoreLink"
        target="_blank"
        rel="noreferrer"
        data-test-id="personal-counter-content-more"
      >
        {{ $t('common.generic.learnmore') }}
      </a>
    </p>
    <!-- Actions of the personal counter content could be it's own component but -->
    <!-- likely to be deleted -->
    <!-- There are three states for the login beta -->
    <!-- Default state with share menu -->
    <!-- Logged in and Logged out state -->
    <div
      v-if="!betaUser"
      class="personal-counter-content__actions personal-counter-content__actions--multiple"
    >
      <nav
        v-show="shareVisible"
        class="personal-counter-content__share"
        role="menu"
        data-test-id="personal-counter-content-share"
      >
        <a
          v-for="item in shareMenu"
          :key="item.label"
          :href="item.url"
          :title="item.label"
          :class="`share-link share-link__${item.id}`"
          role="menuitem"
          target="_blank"
          rel="noopener"
        >
          <component :is="item.id" />
          <span class="sr-only">{{ item.label }}</span>
        </a>
      </nav>
      <a
        v-if="!shareVisible"
        class="link link--primary"
        data-track-id="personal-counter-share"
        data-test-id="personal-counter-content-share-link"
        rel="noopener"
        @click="showShare"
      >
        {{ $t('common.header.counter.share') }}
      </a>
    </div>

    <div
      v-else
      class="personal-counter-content__actions personal-counter-content__actions--multiple"
    >
      <div v-if="!loggedIn" class="beta-login">
        <a
          class="link link--beta"
          href="/account/login"
          data-track-id="personal-counter-content-signin"
          rel="noopener"
        >
          {{ $t('common.header.counter.loggedout.cta') }}
        </a>
      </div>
      <div v-else class="beta-login">
        <ELink
          as="span"
          class="link link--beta"
          data-test-id="personal-counter-content-signout"
          data-track-id="personal-counter-content-signout"
          @click="signOutUser"
        >
          {{ $t('common.header.counter.loggedin.cta') }}
        </ELink>
      </div>
      <EFeedbackLink
        v-bind="feedbackLinkProps"
        :data-track-id="trackIds.feedbackLink"
        data-test-id="feedback"
        class="link"
      >
        {{ $t('common.header.counter.feedback') }}
      </EFeedbackLink>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import EcosiaUser from '@ecosia/ecosia-user';

import { getCookieOptions } from '~~/common/js/universal/utils';
import { getShareMenu } from '~~/common/js/universal/menus';
import { MAIN as MAIN_COOKIE, HEIMDALL as HEIMDALL_COOKIE } from '~~/common/js/constants/cookies';
import {
  ZENDESK_LEARN_MORE_LINK,
  ZENDESK_LEARN_MORE_LINK_BETA,
} from '~~/common/js/constants/urls';
import Facebook from '~~/common/vue/components/icons/m/social/facebook.svg';
import Pinterest from '~~/common/vue/components/icons/m/social/pinterest.svg';
import Twitter from '~~/common/vue/components/icons/m/social/twitter.svg';
import ELink from '~~/common/vue/components/link';
import EFeedbackLink from '~~/common/vue/components/feedback-link';
// this is fixing a warning about missing directive in tests
// TODO: see if we can fix the error another way
// eslint-disable-next-line no-unused-vars
import safeHtml from '~~/common/vue/directives/safe-html';

// TODO: We need to re-think how we want to implement component specific tracking
// adding TRACK_IDs to the component directly makes it difficult to re-use the components.
// It also should not be the concern of the component whats beeing tracked
const TRACK_IDS = {
  beta: {
    loggedIn: {
      learnMoreLink: 'personal-counter-content-learn-more--loggedin',
      feedbackLink: 'personal-counter-content-feedback--loggedin',
    },
    loggedOut: {
      learnMoreLink: 'personal-counter-content-learn-more--loggedout',
      feedbackLink: 'personal-counter-content-feedback--loggedout',
    },
  },
  default: {
    learnMoreLink: 'personal-counter-content-learn-more',
  },
};

export default {
  name: 'EPersonalCounterContent',
  components: {
    ELink,
    EFeedbackLink,
    Facebook,
    Pinterest,
    Twitter,
  },
  data() {
    return {
      shareVisible: false,
    };
  },
  computed: {
    ...mapState('user', [
      'betaUser',
      'loggedIn',
      'loginFailed',
    ]),
    ...mapState('global', [
      'locale',
    ]),
    // TODO: consolidate and DRY with other use-case that defines default values
    feedbackLinkProps() {
      return {
        errorMessage: this.$t('common.header.feedback.error'),
        options: [
          this.$t('common.header.feedback.bullet1'),
          this.$t('common.header.feedback.bullet2'),
          this.$t('common.header.feedback.bullet3'),
          this.$t('common.header.feedback.bullet4'),
        ],
        query: '',
        subjectsPlaceholder: this.$t('common.header.feedback.subject'),
        subjects: [
          this.$t('common.header.feedback.subject1'),
          this.$t('common.header.feedback.subject2'),
          this.$t('common.header.feedback.subject3'),
        ],
        submitText: this.$t('common.header.feedback.send'),
        successMessage: this.$t('common.header.feedback.body2'),
        successTitle: this.$t('common.header.feedback.title2'),
        textareaPlaceholder: this.$t('common.header.feedback.tellmore'),
        title: this.$t('common.header.feedback.login'),
        ticketSubject: 'Index - Personal Counter',
        url: process.env.feedbackUrl,
      };
    },
    shareMenu() {
      const prefix = this.$t('common.header.counter.share.fb.title1');
      const suffix = this.$t('common.header.counter.share.fb.title2');
      const text = encodeURIComponent(`${prefix} ${this.count} ${suffix}`);
      return getShareMenu(text);
    },
    title() {
      if (this.loginFailed) {
        return this.$t('common.generic.error');
      }

      if (this.betaUser && this.loggedIn) {
        return this.$t('common.header.counter.loggedin.title');
      }

      return null;
    },
    bodyText() {
      if (this.loginFailed) {
        return this.$t('common.generic.tryagain');
      }

      const translatedString = this.betaUser ?
        `common.header.counter.logged${this.loggedIn ? 'in' : 'out'}.body` :
        'common.header.counter.body';

      return this.$t(translatedString);
    },
    learnMoreLink() {
      return this.betaUser ?
        ZENDESK_LEARN_MORE_LINK_BETA :
        ZENDESK_LEARN_MORE_LINK;
    },
    trackIds() {
      if (this.betaUser) {
        return this.loggedIn ? TRACK_IDS.beta.loggedIn : TRACK_IDS.beta.loggedOut;
      }

      return TRACK_IDS.default;
    },
  },
  methods: {
    showShare() {
      this.shareVisible = true;
    },
    signOutUser() {
      // Set the cookie to the hostname, plus a leading dot, minus any port
      // eslint-disable-next-line compat/compat
      const domain = `.${window.location.hostname.split(':')[0]}`;
      const options = getCookieOptions(domain);
      const cookieString = this.$cookies.get(MAIN_COOKIE);
      const user = new EcosiaUser({ cookieString });

      this.$cookies.remove(HEIMDALL_COOKIE, options);
      user.set('treeCount', 0);
      window.location.reload(true);
    },
  },
};
</script>

<style lang="scss" scoped>

.personal-counter-content {
  position: relative;

  &__body {
    margin: 0;
    font-size: $font-s;
    line-height: $line-height-m;
    padding: $space-s $space-m 0;
    border-bottom: $border-width;
  }

  &__notify {
    margin: 0;
    font-size: $font-s;
    font-weight: $font-weight-1l;
    line-height: $line-height-m;
    padding: $space-m $space-m 0;

    & + .personal-counter-content__body {
      padding-top: $space-1s;
    }
  }

  &__actions {
    @include divider('top');

    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding-left: $space-1s;
    margin-top: $space-s;
  }

  &__actions--multiple {
    justify-content: space-between;
  }

  &__share {
    margin: $space-s;
  }
}

.link {
  padding: $space-s;
  display: inline-block;
  height: $space-l + ($space-s * 2);
  font-size: $font-s;
  color: $color-text-secondary;
  cursor: pointer;

  &:hover,
  &:focus {
    text-decoration: underline;
  }

  &--beta {
    color: $blue-50;
    padding-right: #{$space-2s + $space-2s};
  }

  &--learn-more {
    padding: 0;
    height: auto;
  }

  &--primary {
    color: $blue-50;
    justify-content: space-between;
  }
}

.share-link {
  display: inline-block;
  margin-right: $space-1s;
  width: $space-l;
}

.share-link__facebook {
  color: $facebook;
}

.share-link__twitter {
  color: $twitter;
}

.share-link__pinterest {
  color: $pinterest;
}

</style>

<docs>
  ```js
  <EPersonalCounterContent />
  ```
</docs>
